import dayjs from 'dayjs'
import {
  isNightStart,
  leaveTypeLabel,
  timeToMinutes,
} from '@/utils/timesheetHelpers'

export { timeToMinutes }

// Pure helpers for the public supervisor approval page. Mirrors the API's
// ShiftTime / SupervisorTimesheetDay maths so the totals the supervisor sees
// while editing match what the server stores (the server stays the authority).

export const DECLARATION = (name) =>
  `I, ${name}, confirm that the hours recorded on this timesheet are true and correct.`

// A finish earlier than the start is an overnight shift, same as the API.
export function spanMinutes(start, finish) {
  const s = timeToMinutes(start)
  const f = timeToMinutes(finish)
  if (s === null || f === null) return 0
  return f >= s ? f - s : f + 1440 - s
}

export function workedMinutes(day) {
  if (!day || !day.start || !day.finish) return 0
  return Math.max(
    0,
    spanMinutes(day.start, day.finish) - (Number(day.break_minutes) || 0),
  )
}

// Read-only displays and exports use the hours the API already worked out
// (ShiftTime::dayFields). Only screens where the user is typing a time still
// work it out here, because their edits are not saved yet. The fallback covers
// an older API that does not send the fields.
export function dayWorkedMinutes(day) {
  if (day && typeof day.worked_minutes === 'number') return day.worked_minutes
  return day && day.present === false ? 0 : workedMinutes(day)
}

export function dayIsNight(day) {
  if (day && typeof day.is_night === 'boolean') return day.is_night
  return isNightStart(day && day.start, day && day.finish)
}

export function totalMinutes(days) {
  return (days || []).reduce((sum, d) => sum + workedMinutes(d), 0)
}

export function formatMinutes(minutes) {
  const m = Math.abs(Math.round(minutes || 0))
  const h = Math.floor(m / 60)
  const rest = m % 60
  if (h === 0 && rest === 0) return '0h'
  if (rest === 0) return `${h}h`
  return `${h}h ${rest}m`
}

export function formatDifference(minutes) {
  if (!minutes) return 'no change'
  return `${minutes > 0 ? '+' : '-'}${formatMinutes(minutes)}`
}

// Compared by clock time and break, not by string, so "7:00" equals "07:00".
export function dayChanged(original, current) {
  return (
    timeToMinutes(original.start) !== timeToMinutes(current.start) ||
    timeToMinutes(original.finish) !== timeToMinutes(current.finish) ||
    (Number(original.break_minutes) || 0) !==
      (Number(current.break_minutes) || 0)
  )
}

export function plantChanged(original, current) {
  return Number(original.quantity) !== Number(current.quantity)
}

// Same rules the API enforces, caught before the round trip. Returns a message
// or null.
export function dayError(day) {
  const hasStart = !!day.start
  const hasFinish = !!day.finish
  if (hasStart !== hasFinish)
    return 'Enter both a start and a finish, or clear both.'
  if (!hasStart) return null
  if (timeToMinutes(day.start) === null || timeToMinutes(day.finish) === null) {
    return 'Times must look like 07:00.'
  }
  if (day.start === day.finish) return 'Start and finish cannot be the same.'
  const brk = Number(day.break_minutes) || 0
  if (brk < 0) return 'Break cannot be negative.'
  if (brk >= spanMinutes(day.start, day.finish)) {
    return 'The break is longer than the shift.'
  }
  return null
}

export function plantError(plant) {
  const q = Number(plant.quantity)
  if (plant.quantity === '' || plant.quantity === null || Number.isNaN(q)) {
    return 'Enter a quantity.'
  }
  if (q < 0) return 'Quantity cannot be negative.'
  return null
}

export function describeApiError(error, fallback) {
  const data = error && error.response && error.response.data
  if (data && data.errors) {
    const first = Object.values(data.errors)[0]
    if (first && first[0]) return first[0]
  }
  if (data && data.message) return data.message
  return fallback
}

const EXPORT_DECISIONS = {
  approved: 'Approved',
  amended: 'Approved with changes',
  rejected: 'Rejected',
}

// The external supervisor's CSV/PDF rows, one per worked or leave day, laid
// out like the company supervisor's export (buildSupervisorExportRows). Each
// sheet is { project_name, tradie_name, week_start, decision, days } with the
// days the supervisor saw: their changed hours when they made changes. Leave
// hours stay blank there too; the Type column names the leave.
export function buildExternalExportRows(sheets) {
  const fmt = (d) => dayjs(d).format('DD/MM/YYYY')
  const rows = [
    [
      'Project',
      'Week Start',
      'Employee',
      'Date',
      'Day',
      'Start',
      'Finish',
      'Break (mins)',
      'Type',
      'Hours',
      'Status',
      'Day / Night',
    ],
  ]
  ;(sheets || []).forEach((sheet) => {
    const status = EXPORT_DECISIONS[sheet.decision] || 'Waiting for approval'
    ;(sheet.days || []).forEach((day) => {
      const leave = !!day.leave_type
      const minutes = dayWorkedMinutes(day)
      if (!leave && minutes <= 0) return
      rows.push([
        sheet.project_name || '',
        fmt(sheet.week_start),
        sheet.tradie_name || '',
        fmt(day.date),
        dayjs(day.date).format('dddd'),
        leave ? '' : day.start || '',
        leave ? '' : day.finish || '',
        leave ? '' : Number(day.break_minutes) || 0,
        leave ? leaveTypeLabel(day.leave_type) || 'Leave' : 'Onsite',
        leave ? '' : Math.round((minutes / 60) * 100) / 100,
        status,
        leave ? '' : dayIsNight(day) ? 'Night' : 'Day',
      ])
    })
  })
  return rows
}
